import { useManageCriteria } from '~/common/api';
import { useI18n, Trans } from '~/common/context';
import { useCommonErrorTranslation } from '~/common/hooks';
import { notify, useConfirmation } from '@wyz/ui';
import { ValidationErrorResponse } from '@wyz/types';
import { CommonErrorCodesEnumType } from '~/common/types';
import { CommonErrorCodes } from '~/common/helpers';
import { CriteriaHelpers } from '~/connect/helpers';
import {
  ICriteria,
  ICriteriaPayload,
  ICriteriaValueUsageError,
} from '~/connect/types';

interface UseCriteriaActionsProps {
  criteriaData?: ICriteria[];
}

// the api only sends the counts it checked, missing ones are displayed as 0
const getUsageCounts = (meta: Partial<Record<string, number>>) => ({
  countCustomer: meta.CUSTOMER ?? 0,
  countSettingTemplate: meta.SETTING_TEMPLATE ?? 0,
  countSupplierTemplate: meta.SUPPLIER_TEMPLATE ?? 0,
  countBrandTemplate: meta.BRAND_TEMPLATE ?? 0,
  countAdvert: meta.ADVERT ?? 0,
  countTheme: meta.THEME ?? 0,
  countQuoteBrandTemplate: meta.QUOTE_BRAND_TEMPLATE ?? 0,
  countRedistributionBrandTemplate: meta.REDISTRIBUTION_BRAND_TEMPLATE ?? 0,
  countRedistributionSupplierTemplate:
    meta.REDISTRIBUTION_SUPPLIER_TEMPLATE ?? 0,
  countWheelSupplierTemplate: meta.WHEEL_SUPPLIER_TEMPLATE ?? 0,
  countS2sFlowTemplate: meta.S2S_FLOW_TEMPLATE ?? 0,
});

export const useCriteriaActions = ({
  criteriaData,
}: UseCriteriaActionsProps) => {
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const ask = useConfirmation();

  const manageCriteria = useManageCriteria({
    create: { enabled: true },
    remove: { enabled: true },
    update: { enabled: true },
  });

  const alertErrors = (
    error: ValidationErrorResponse<CommonErrorCodesEnumType>,
  ) => {
    error.errors.forEach(({ errors }) =>
      errors.forEach((error) => {
        notify.error(<Trans i18nKey={resolveErrorKey(error)} />, {
          id: 'toast-error-criteria-validation',
        });
      }),
    );
  };

  const handleToggleVisibility = (id: string, visible: boolean) => {
    const criteria = criteriaData?.find((c) => c.id === id);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id,
        body: {
          visible,
        },
      },
      {
        onSuccess: () => {
          notify.success(<Trans i18nKey='app.criteria.create.success' />, {
            id: 'toast-success-criteria-updated',
          });
        },
        onError: () => {
          notify.error(<Trans i18nKey='app.common.messages.error' />, {
            id: 'toast-error-criteria-visibility',
          });
        },
      },
    );
  };

  const alertValuesInUse = (error: {
    message: string;
    meta: ICriteriaValueUsageError;
  }) => {
    notify.error(
      <Trans
        i18nKey={resolveErrorKey(error.message)}
        values={{
          countCustomer: error.meta.CUSTOMER,
          countSettingTemplate: error.meta.SETTING_TEMPLATE,
          countSupplierTemplate: error.meta.SUPPLIER_TEMPLATE,
          countBrandTemplate: error.meta.BRAND_TEMPLATE,
          countAdvert: error.meta.ADVERT,
          countTheme: error.meta.THEME,
          countQuoteBrandTemplate: error.meta.QUOTE_BRAND_TEMPLATE,
          countRedistributionBrandTemplate:
            error.meta.REDISTRIBUTION_BRAND_TEMPLATE,
          countRedistributionSupplierTemplate:
            error.meta.REDISTRIBUTION_SUPPLIER_TEMPLATE,
          countWheelSupplierTemplate: error.meta.WHEEL_SUPPLIER_TEMPLATE,
          countS2sFlowTemplate: error.meta.S2S_FLOW_TEMPLATE,
        }}
      />,
      {
        id: 'toast-error-criteria-value-delete-in-use',
      },
    );
  };

  const handleConfigureCriteria = (
    id: string,
    payload: ICriteriaPayload,
    onSuccess?: () => void,
  ) => {
    manageCriteria.update.mutate(
      { id, body: payload },
      {
        onSuccess: () => {
          onSuccess?.();
          notify.success(<Trans i18nKey='app.criteria.configure.success' />, {
            id: 'toast-success-criteria-configured',
          });
        },
        onError: (error) => {
          if (error.message === CommonErrorCodes.ERR_CRITERIA_VALUES_IN_USE) {
            alertValuesInUse(error);
            return;
          }
          alertErrors(error);
        },
      },
    );
  };

  const handleDeleteCriteria = (id: string) => {
    const criteria = criteriaData?.find((c) => c.id === id);
    if (!criteria) return;

    ask({
      title: t('app.criteria.delete_criterion.confirmation.title', {
        criterion: criteria.name,
      }),
      description: t('app.criteria.delete_criterion.confirmation.description', {
        criterion: criteria.name,
      }),
      cancelBtnText: t('app.common.button.cancel'),
      confirmBtnText: t('app.common.buttons.delete'),
      confirmBtnVariant: 'danger',
    }).onConfirm(() => {
      manageCriteria.remove.mutate(id, {
        onSuccess: () =>
          notify.success(
            <Trans i18nKey='app.criteria.list.actions.delete.success' />,
            {
              id: 'toast-success-criteria-deleted',
            },
          ),
        onError: (error) => {
          if (error.message === CommonErrorCodes.ERR_CRITERION_IN_USE) {
            notify.error(
              <Trans
                i18nKey={resolveErrorKey(error.message)}
                values={getUsageCounts(error.meta)}
              />,
              {
                id: 'toast-error-criteria-delete-in-use',
              },
            );
          } else {
            notify.error(
              <Trans i18nKey='app.criteria.list.actions.delete.error' />,
              {
                id: 'toast-error-criteria-delete-failed',
              },
            );
          }
        },
      });
    });
  };

  const handleUpdateValue = (
    criteriaId: string,
    valueIndex: number,
    newValue: string,
  ) => {
    const criteria = criteriaData?.find((c) => c.id === criteriaId);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id: criteriaId,
        body: {
          ...criteria,
          values: criteria.values.map(
            (item: ICriteria['values'][number], _index: number) =>
              valueIndex !== _index ? item : { ...item, label: newValue },
          ),
        },
      },
      {
        onError: alertErrors,
      },
    );
  };

  const handleDeleteValue = (criteriaId: string, valueIndex: number) => {
    const criteria = criteriaData?.find((c) => c.id === criteriaId);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id: criteriaId,
        body: {
          ...criteria,
          values: criteria.values.filter((_, _index) => valueIndex !== _index),
          visibilityRules: CriteriaHelpers.removeValueFromRules(
            criteria.visibilityRules,
            criteria.values[valueIndex].id,
          ),
        },
      },
      {
        onError(error) {
          if (error.message === CommonErrorCodes.ERR_CRITERIA_VALUES_IN_USE) {
            notify.error(
              <Trans
                i18nKey={resolveErrorKey(error.message)}
                values={getUsageCounts(error.meta)}
              />,
              {
                id: 'toast-error-criteria-value-delete-in-use',
              },
            );
          }
        },
      },
    );
  };

  const handleAddValue = (criteriaId: string, newValue: string) => {
    const criteria = criteriaData?.find((c) => c.id === criteriaId);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id: criteriaId,
        body: {
          ...criteria,
          values: [...criteria.values, { label: newValue }],
        },
      },
      {
        onError: alertErrors,
      },
    );
  };

  const handleCreateCriteria = (
    payload: ICriteriaPayload,
    onSuccess?: () => void,
  ) => {
    manageCriteria.create.mutate(payload, {
      onSuccess: () => {
        onSuccess?.();
        notify.success(<Trans i18nKey='app.criteria.create.success' />, {
          id: 'toast-success-criteria-created',
        });
      },
      onError: alertErrors,
    });
  };

  return {
    actions: {
      toggleVisibility: handleToggleVisibility,
      configure: handleConfigureCriteria,
      delete: handleDeleteCriteria,
      updateValue: handleUpdateValue,
      deleteValue: handleDeleteValue,
      addValue: handleAddValue,
      create: handleCreateCriteria,
    },
    isCreating: manageCriteria.create.isPending,
    isUpdating: manageCriteria.update.isPending,
  };
};
